/**
 * Air Traffic Control Page
 *
 * Launch, monitor, terminate, and delete project pipelines.
 */

import React, { useState } from 'react';
import { Card, Stack, Flex, Button, useTheme, spacing, textStyles, fontSize } from '@fti/design-system';
import { usePipelineStatus, usePipelineDataSources, usePipelineMutations } from '../../api/pipelines';
import { useHierarchy } from '../../context/HierarchyContext';
import { LoadingState } from '../shared/LoadingState';
import { ErrorDisplay } from '../shared/ErrorDisplay';
import { ConfirmModal } from '../shared/ConfirmModal';
import { RoleGate } from '../shared/RoleGate';
import { GATES } from '../../auth/roles';
import { errorMessage } from '../../api/errors';

export function AirTrafficControlPage() {
  const { theme } = useTheme();
  const { selectedClient, selectedMatter, selectedProject } = useHierarchy();
  const { data: pipeline, loading, error, refetch } = usePipelineStatus(selectedProject?.id);
  const { data: dataSources, loading: loadingDS } = usePipelineDataSources(selectedProject?.id);
  const { launch, terminate, deletePipeline } = usePipelineMutations();

  const [launching, setLaunching] = useState(false);
  const [terminating, setTerminating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const borderColor = theme.colors.border.default;

  const handleLaunch = async () => {
    if (!selectedProject) return;
    setLaunching(true);
    setErrorMsg('');
    try {
      await launch(selectedProject.id);
      refetch();
    } catch (err) {
      setErrorMsg(errorMessage(err, 'Failed to launch pipeline.'));
    } finally {
      setLaunching(false);
    }
  };

  const handleTerminate = async () => {
    if (!selectedProject) return;
    setTerminating(true);
    setErrorMsg('');
    try {
      await terminate(selectedProject.id);
      refetch();
    } catch (err) {
      setErrorMsg(errorMessage(err, 'Failed to terminate pipeline.'));
    } finally {
      setTerminating(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedProject) return;
    setShowDeleteConfirm(false);
    setErrorMsg('');
    try {
      await deletePipeline(selectedProject.id);
      refetch();
    } catch (err) {
      setErrorMsg(errorMessage(err, 'Failed to delete pipeline.'));
    }
  };

  const statusColor = (status: string | undefined) => {
    switch (status) {
      case 'running': return theme.colors.primary[600];
      case 'completed': return theme.colors.semantic.success.default;
      case 'failed': return theme.colors.semantic.error.default;
      case 'terminated': return theme.colors.semantic.warning.default;
      default: return mutedColor;
    }
  };

  if (!selectedProject) {
    return (
      <Stack gap="lg">
        <h1 style={{ ...textStyles.h3, color: textColor }}>Air Traffic Control</h1>
        <Card variant="outlined">
          <Card.Body>
            <Flex direction="column" align="center" style={{ padding: spacing.lg }}>
              <span style={{ color: mutedColor, fontSize: fontSize.sm }}>
                Select a client, matter, and project from the context bar to manage pipelines.
              </span>
            </Flex>
          </Card.Body>
        </Card>
      </Stack>
    );
  }

  if (loading || loadingDS) return <LoadingState message="Loading pipeline status..." />;
  if (error) return <ErrorDisplay error={error} onRetry={refetch} />;

  const isRunning = pipeline?.status === 'running';
  const canLaunch = !pipeline || pipeline.status === 'configured' || pipeline.status === 'completed' || pipeline.status === 'failed' || pipeline.status === 'terminated';
  const canTerminate = isRunning;
  const canDelete = pipeline && pipeline.status !== 'running';

  return (
    <Stack gap="lg">
      <Flex justify="between" align="center">
        <div>
          <h1 style={{ ...textStyles.h3, color: textColor }}>Air Traffic Control</h1>
          <p style={{ margin: 0, marginTop: spacing.xs, fontSize: fontSize.sm, color: mutedColor }}>
            Project: {selectedProject.name} (v{selectedProject.version})
          </p>
        </div>
        <RoleGate minRole={GATES.pipelineControl}>
          <Flex gap="sm">
            <Button variant="solid" size="sm" onClick={handleLaunch} disabled={!canLaunch || launching}>
              {launching ? 'Launching...' : 'Launch Pipeline'}
            </Button>
            <Button variant="outline" color="warning" size="sm" onClick={handleTerminate} disabled={!canTerminate || terminating}>
              {terminating ? 'Terminating...' : 'Terminate'}
            </Button>
            <Button variant="outline" color="error" size="sm" onClick={() => setShowDeleteConfirm(true)} disabled={!canDelete}>
              Delete
            </Button>
          </Flex>
        </RoleGate>
      </Flex>

      {errorMsg && (
        <span style={{ color: theme.colors.semantic.error.default, fontSize: fontSize.sm }}>{errorMsg}</span>
      )}

      {/* Pipeline Status */}
      <Card variant="elevated">
        <Card.Body>
          <Stack gap="md">
            <span style={{ fontWeight: 600, color: textColor, fontSize: fontSize.sm, textTransform: 'uppercase' }}>
              Pipeline Status
            </span>
            {pipeline ? (
              <div style={{ border: `1px solid ${borderColor}`, borderRadius: '8px', padding: spacing.md }}>
                <Flex gap="lg" align="center">
                  <div>
                    <span style={{ fontSize: fontSize.xs, color: mutedColor, display: 'block' }}>Status</span>
                    <span style={{
                      fontSize: fontSize.sm,
                      fontWeight: 600,
                      color: statusColor(pipeline.status),
                      textTransform: 'capitalize',
                    }}>
                      {pipeline.status}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: fontSize.xs, color: mutedColor, display: 'block' }}>id_client</span>
                    <span style={{ fontSize: fontSize.sm, fontFamily: 'monospace', color: textColor }}>
                      {selectedClient?.id ?? '—'}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: fontSize.xs, color: mutedColor, display: 'block' }}>id_matter</span>
                    <span style={{ fontSize: fontSize.sm, fontFamily: 'monospace', color: textColor }}>
                      {selectedMatter?.id ?? '—'}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: fontSize.xs, color: mutedColor, display: 'block' }}>id_project</span>
                    <span style={{ fontSize: fontSize.sm, fontFamily: 'monospace', color: textColor }}>
                      {pipeline.id_project ?? selectedProject?.id ?? '—'}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: fontSize.xs, color: mutedColor, display: 'block' }}>DAG Run ID</span>
                    <span style={{ fontSize: fontSize.sm, fontFamily: 'monospace', color: textColor }}>
                      {pipeline.dag_run_id || '—'}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: fontSize.xs, color: mutedColor, display: 'block' }}>Run Started</span>
                    <span style={{ fontSize: fontSize.sm, color: textColor }}>
                      {pipeline.run_started_at ? new Date(pipeline.run_started_at).toLocaleString() : '—'}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: fontSize.xs, color: mutedColor, display: 'block' }}>Run Completed</span>
                    <span style={{ fontSize: fontSize.sm, color: textColor }}>
                      {pipeline.run_completed_at ? new Date(pipeline.run_completed_at).toLocaleString() : '—'}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: fontSize.xs, color: mutedColor, display: 'block' }}>Date Range</span>
                    <span style={{ fontSize: fontSize.sm, color: textColor }}>
                      {pipeline.date_range_start ? new Date(pipeline.date_range_start).toLocaleDateString() : '—'}
                      {' – '}
                      {pipeline.date_range_end ? new Date(pipeline.date_range_end).toLocaleDateString() : 'Ongoing'}
                    </span>
                  </div>
                </Flex>
                {pipeline.error_message && (
                  <div style={{ marginTop: spacing.sm, padding: spacing.sm, background: theme.colors.semantic.error.light + '15', borderRadius: '4px' }}>
                    <span style={{ fontSize: fontSize.xs, color: theme.colors.semantic.error.default }}>{pipeline.error_message}</span>
                  </div>
                )}
              </div>
            ) : (
              <span style={{ color: mutedColor, fontSize: fontSize.sm }}>No pipeline configured for this project.</span>
            )}
          </Stack>
        </Card.Body>
      </Card>

      {/* Data Sources */}
      <Card variant="elevated">
        <Card.Body>
          <Stack gap="sm">
            <span style={{ fontWeight: 600, color: textColor, fontSize: fontSize.sm, textTransform: 'uppercase' }}>
              Registered Data Sources
            </span>
            {dataSources && dataSources.length > 0 ? (
              <div style={{ border: `1px solid ${borderColor}`, borderRadius: '8px', overflow: 'hidden' }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 120px',
                  padding: `${spacing.sm} ${spacing.md}`,
                  background: theme.colors.background.paper,
                  borderBottom: `1px solid ${borderColor}`,
                  fontSize: fontSize.xs,
                  fontWeight: 600,
                  color: mutedColor,
                  textTransform: 'uppercase',
                }}>
                  <span>Name</span>
                  <span>Type</span>
                </div>
                {dataSources.map((ds) => (
                  <div key={ds.id} style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 120px',
                    padding: `${spacing.sm} ${spacing.md}`,
                    borderBottom: `1px solid ${borderColor}`,
                    fontSize: fontSize.sm,
                    alignItems: 'center',
                  }}>
                    <span style={{ color: textColor }}>{ds.name}</span>
                    <span style={{
                      fontSize: fontSize.xs,
                      padding: '2px 6px',
                      borderRadius: '3px',
                      background: `${theme.colors.primary[600]}10`,
                      color: theme.colors.primary[600],
                      fontWeight: 600,
                      width: 'fit-content',
                    }}>
                      {ds.type}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <span style={{ color: mutedColor, fontSize: fontSize.sm }}>
                No data sources registered. Add sources from the LexisNexis or Brandwatch pages.
              </span>
            )}
          </Stack>
        </Card.Body>
      </Card>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Pipeline"
        message="Are you sure you want to delete this pipeline and all its resources? This cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </Stack>
  );
}
